import { spreadAndtransformDefaultValues } from '@common/components/forms';
import { IClientVerificationRequest } from "@common/models";
import { Page } from '@customer/components/layout/.';
import { observer } from 'mobx-react';
import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { ValidationViewStore } from './ValidationViewStore';

interface IValidationViewProps {
	store: ValidationViewStore;
}

export const ValidationView = observer(({ store }: IValidationViewProps) => {

	const defaultValues = store.clientVerificationRequest;

	const { register, handleSubmit, control, formState: { errors } } = useForm<IClientVerificationRequest>({
		mode: "onBlur",
		shouldFocusError: true,
		defaultValues: spreadAndtransformDefaultValues(defaultValues)
	});

	return (
		<>
			<Page>
				<Page.Content>
					{!store.verifySent ?
						<Form noValidate className="validationForm" onSubmit={handleSubmit(m => store.validateCustomer(m))}>
							<Row>
								<Col xs={12}>
									<h3 className="fw-bold">Managing your Vetco experience is easy!</h3>
								</Col>
								<Col xs={12}>
									<p>Give us your email or phone number used when booking your appointment or during your visit and we will send you a link. To manage your communication preferences, please validate with the phone number used to book your appointment.</p>
								</Col>
							</Row>

							<Row>
								<Col xs={12}>
									<Form.Group className="mb-3" controlId="formEmail">
										<Form.Control {...register('email', {
											pattern: {
												value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
												message: "Please enter a valid email address"
											}
										})}
											type="email"
											placeholder="Email"
											isInvalid={errors.email ? true : false}
										/>
										<Form.Control.Feedback type="invalid">
											{errors.email && <span role="alert">{errors.email.message}</span>}
										</Form.Control.Feedback>
									</Form.Group>
									<p className="mb-3">-or-</p>
									<Form.Group className="mb-4" controlId="formPhone">
										<Controller
											name="phone"
											control={control}
											rules={{
												pattern: { value: /^(\([0-9]{3}\)|[0-9]{3}-)[0-9]{3}-[0-9]{4}$/i, message: "Please enter a valid phone number" },
												minLength: { value: 10, message: "Phone number must be 10 digits" }
											}}
											render={({ field: { onChange, value }, fieldState: { invalid } }) => (
												<PatternFormat
													type="tel"
													customInput={Form.Control}
													value={value}
													format="###-###-####"
													mask="x"
													placeholder="Phone #"
													onValueChange={(v) => onChange(v.formattedValue)}
													aria-invalid={invalid}
													isInvalid={invalid}
												/>
											)}
										/>
										<Form.Control.Feedback type="invalid">
											{errors.phone && <span role="alert">{errors.phone.message}</span>}
										</Form.Control.Feedback>
									</Form.Group>
								</Col>
							</Row>
							<Row>
								<Col xs={12}>
									<p>
										By clicking &ldquo;Send Validation,&rdquo; I agree to receive text messages about my pet&rsquo;s veterinary services. Texts may be made using automated telephone dialing systems. Not a condition for purchase. I agree to Petco&rsquo;s <a
											className='link-revert' target="_blank" rel="noreferrer noopener"
											href="https://www.petco.com/content/petco/PetcoStore/en_US/pet-services/terms-of-use.html">
											Terms of Use
										</a>, <a
											className='link-revert' target="_blank" rel="noreferrer noopener"
											href="https://www.petco.com/privacy-policy">
											Privacy Policy
										</a> & <a
											className='link-revert' target="_blank" rel="noreferrer noopener"
											href="https://www.petco.com/content/petco/PetcoStore/en_US/pet-services/terms-of-use/sms-terms-conditions.html">
											SMS Terms & Conditions
										</a>.
									</p>
								</Col>
							</Row>
							<Row>
								<Col xs={12}>
									<div className="d-grid">
										<Button type="submit" className="text-uppercase">Send Validation</Button>
									</div>
								</Col>
								{
									!store.isValid ?
										<Col xs={12} className="mt-3" data-testid="response-rejected">
											<span className="text-danger">Please provide your email or phone number used when booking your appointment or during your visit. Only one may be applied for verification.</span>
										</Col>
										: null
								}
							</Row>
						</Form>
						:
						<Row>
							<Col xs={12}>
								<h3 className="header" data-testid="response-sent">If your account is found using the information provided, a link will be sent to {!store.clientVerificationRequest.email ? store.clientVerificationRequest.phone : store.clientVerificationRequest.email}</h3>
							</Col>
						</Row>
					}
				</Page.Content>
			</Page>
		</>
	)
});
